// browser-image-compression (~120KB) is loaded on first use, not at startup
const loadImageCompression = () => import('browser-image-compression').then((m) => m.default);

const IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
];

// Profile pictures: 400x400 max, ~25-50KB target, JPEG
export const AVATAR_OPTIONS = {
  maxSizeMB: 0.1,
  maxWidthOrHeight: 400,
  useWebWorker: true,
  fileType: 'image/jpeg',
  initialQuality: 0.82,
};

// Profile & Community banners: 1200x450 max, ~60-120KB target, JPEG
export const HEADER_OPTIONS = {
  maxSizeMB: 0.25,
  maxWidthOrHeight: 1200,
  useWebWorker: true,
  fileType: 'image/jpeg',
  initialQuality: 0.80,
};

// Post feed media: 1440px max, ~70-160KB target, JPEG
export const POST_OPTIONS = {
  maxSizeMB: 0.35,
  maxWidthOrHeight: 1440,
  useWebWorker: true,
  fileType: 'image/jpeg',
  initialQuality: 0.80,
};

// Stories: 1080px max dimension, ~100-180KB target, JPEG
export const STORY_OPTIONS = {
  maxSizeMB: 0.35,
  maxWidthOrHeight: 1080,
  useWebWorker: true,
  fileType: 'image/jpeg',
  initialQuality: 0.82,
};

// Direct messages: 1200px max, ~60-120KB target, JPEG
export const MESSAGE_OPTIONS = {
  maxSizeMB: 0.25,
  maxWidthOrHeight: 1200,
  useWebWorker: true,
  fileType: 'image/jpeg',
  initialQuality: 0.80,
};

export const DEFAULT_OPTIONS = POST_OPTIONS;

// Older Safari silently falls back to PNG when asked for WebP, which would be larger than JPEG
const canEncodeWebp = (() => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').startsWith('data:image/webp');
  } catch {
    return false;
  }
})();
const OUTPUT_TYPE = canEncodeWebp ? 'image/webp' : 'image/jpeg';
const OUTPUT_EXT = canEncodeWebp ? '.webp' : '.jpg';

/**
 * Resolves compression options based on category or legacy options
 */
function getCompressionOptions(options = {}) {
  return { ...resolveCategoryOptions(options), fileType: OUTPUT_TYPE };
}

function resolveCategoryOptions(options) {
  if (options.type === 'avatar' || options.profile === true) {
    return AVATAR_OPTIONS;
  }
  if (options.type === 'header') {
    return HEADER_OPTIONS;
  }
  if (options.type === 'story') {
    return STORY_OPTIONS;
  }
  if (options.type === 'message') {
    return MESSAGE_OPTIONS;
  }
  if (options.type === 'post') {
    return POST_OPTIONS;
  }
  return { ...DEFAULT_OPTIONS, ...options };
}

/**
 * Compress an image file for upload. Converts to WebP (or JPEG where the browser can't encode WebP). Non-image files (e.g. video) are returned unchanged.
 * @param {File} file
 * @param {{ type?: 'avatar'|'header'|'post'|'story'|'message', profile?: boolean, maxSizeMB?: number, maxWidthOrHeight?: number }} options
 * @returns {Promise<File>}
 */
export async function compressImageIfNeeded(file, options = {}) {
  if (!file || !IMAGE_TYPES.includes(file.type)) {
    return file;
  }
  // Skip compression for GIF to preserve multi-frame animation
  if (file.type === 'image/gif') {
    return file;
  }
  try {
    const opts = getCompressionOptions(options);
    const imageCompression = await loadImageCompression();
    const compressed = await imageCompression(file, opts);

    const type = compressed.type || OUTPUT_TYPE;
    const ext = { 'image/webp': '.webp', 'image/png': '.png', 'image/jpeg': '.jpg' }[type] || OUTPUT_EXT;
    const baseName = (compressed.name || file.name || 'image').replace(/\.[^/.]+$/, '') || 'image';
    return new File([compressed], `${baseName}${ext}`, { type, lastModified: Date.now() });
  } catch (err) {
    console.warn('Image compression fallback to original:', err);
    return file;
  }
}

/**
 * Compress multiple files; only images are compressed, others (e.g. video) are kept as-is.
 * @param {File[]} files
 * @param {Object} options
 * @returns {Promise<File[]>}
 */
export async function compressImageFiles(files, options = {}) {
  return Promise.all(files.map((file) => compressImageIfNeeded(file, options)));
}

/**
 * Compress images and videos before upload. Images use image compression; videos use FFmpeg.wasm (lazy-loaded, one at a time).
 * Non-media files are returned unchanged.
 * @param {File[]} files
 * @param {Object} options - Options passed to compressImageIfNeeded
 * @returns {Promise<File[]>}
 */
export async function compressMediaFiles(files, options = {}) {
  const { compressVideoIfNeeded } = await import('./videoCompression');
  const result = [];
  for (const file of files) {
    if (!file) {
      result.push(file);
      continue;
    }
    const baseType = (file.type || '').split(';')[0];
    if (baseType.startsWith('video/')) {
      result.push(await compressVideoIfNeeded(file, options));
    } else {
      result.push(await compressImageIfNeeded(file, options));
    }
  }
  return result;
}
